import { ApiError, api } from '../api/client.ts';
import { deviceStore, DRAFT_TTL_MS, draftStore, QUEUE_KEY_PREFIX } from '../patients/drafts.ts';

// The retry queue (plan §7 Offline; ADR 0018): a few low-risk writes made while offline, or
// when the connection drops mid-save, are kept on this device and sent when Wodi can be
// reached, each with an idempotency key so a replay can't apply twice. Only the kinds in
// QUEUEABLE may be queued; payments, dispensing, orders, prescriptions, signing, stock and
// anything else that must not happen late or twice are never queued.
//
// Items are per user and facility (the storage key says so), hold PHI like drafts do, and
// are deleted on sign-out and idle timeout with every draft. An item older than a draft may
// live (DRAFT_TTL_MS) is not sent: it is marked failed with the reason, for the person to
// check and enter again.

export type QueuedKind = 'triage' | 'note-draft';

/** Where each kind may be sent, and how the server makes a replay harmless. */
export const QUEUEABLE: Record<
  QueuedKind,
  { method: 'POST' | 'PUT'; path: RegExp; idempotency: string }
> = {
  // The body's clientRequestId (the item id) makes the server return the stored record.
  triage: {
    method: 'POST',
    path: /^\/visits\/[0-9a-f-]{36}\/triage$/,
    idempotency: 'clientRequestId in the body',
  },
  // The Idempotency-Key header (the item id).
  'note-draft': {
    method: 'PUT',
    path: /^\/consultations\/[0-9a-f-]{36}\/note\/draft$/,
    idempotency: 'Idempotency-Key header',
  },
};

export type QueuedStatus = 'waiting' | 'failed';

export interface QueuedError {
  code: string;
  message: string;
  /** From the server's error context: IDs and codes only (e.g. alert rule IDs). */
  ruleIds: string[];
}

export interface QueuedAction {
  /** Also the idempotency key. */
  id: string;
  kind: QueuedKind;
  /** What it is for, e.g. the visit or consultation ID. One queued item per kind and target. */
  target: string;
  /** Plain words for the list, never patient data: "Triage", "Consultation note draft". */
  label: string;
  /** Where to open it to check or finish it. IDs only. */
  href: string;
  method: 'POST' | 'PUT';
  path: string;
  body: unknown;
  /** When it was saved on this device (ISO). */
  savedAt: string;
  status: QueuedStatus;
  attempts: number;
  error: QueuedError | null;
  /** The draft to delete once it is sent. */
  clearDraftKey: string | null;
  /** React Query keys to refresh once it is sent. */
  refresh: string[][];
  /** The toast once it is sent, or null to stay quiet (autosave). */
  sentTitle: string | null;
  /** Something to say about it while it waits (e.g. an urgent triage category). */
  urgent: boolean;
}

export type NewQueuedAction = Omit<
  QueuedAction,
  'id' | 'savedAt' | 'status' | 'attempts' | 'error'
> & { id?: string };

/** The error of an item kept past DRAFT_TTL_MS: never sent, shown with its next step. */
export const EXPIRED_ERROR: QueuedError = {
  code: 'expired',
  message:
    'Saved on this device more than 12 hours ago, so it was not sent. Check the record in Wodi and enter it again if it is missing.',
  ruleIds: [],
};

function isExpired(item: QueuedAction, now: number): boolean {
  const saved = Date.parse(item.savedAt);
  return Number.isNaN(saved) || now - saved > DRAFT_TTL_MS;
}

/** After this many failed tries on a server or unexpected error, the item stops and asks. */
export const MAX_ATTEMPTS = 5;

export class NotQueueableError extends Error {}

type Listener = () => void;

export interface SendResult {
  sent: QueuedAction[];
}

function isNetworkPause(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.code === 'offline' || error.status === 401 || error.code === 'not_signed_in')
  );
}

function isTransient(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  return error.status >= 500 || error.status === 429 || error.code === 'unexpected_response';
}

function errorOf(error: unknown): QueuedError {
  if (error instanceof ApiError) {
    const ids = error.context?.['ruleIds'];
    return {
      code: error.code,
      message: error.message,
      ruleIds: Array.isArray(ids) ? ids.filter((x): x is string => typeof x === 'string') : [],
    };
  }
  return { code: 'unexpected_response', message: 'Something went wrong. Try again.', ruleIds: [] };
}

export class RetryQueue {
  private scope: string | null = null;
  private items: readonly QueuedAction[] = [];
  private readonly listeners = new Set<Listener>();
  private flushing: Promise<SendResult> | null = null;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** The items for the person signed in here, oldest first. The same array until it changes. */
  snapshot = (): readonly QueuedAction[] => this.items;

  private key(id: string): string {
    return `${QUEUE_KEY_PREFIX}${this.scope ?? ''}:${id}`;
  }

  private publish(items: readonly QueuedAction[]): void {
    this.items = [...items].sort((a, b) => a.savedAt.localeCompare(b.savedAt));
    for (const listener of this.listeners) listener();
  }

  /** Reads the queue of this user at this facility from the device. */
  async load(userId: string, facilityId: string): Promise<void> {
    const scope = `${userId}:${facilityId}`;
    this.scope = scope;
    const store = deviceStore();
    const prefix = `${QUEUE_KEY_PREFIX}${scope}:`;
    const found: QueuedAction[] = [];
    const now = Date.now();
    try {
      for (const key of await store.keys()) {
        if (!key.startsWith(prefix)) continue;
        const item = (await store.get(key)) as QueuedAction | undefined;
        if (item === undefined || !(item.kind in QUEUEABLE)) continue;
        if (item.status === 'waiting' && isExpired(item, now)) {
          const expired: QueuedAction = { ...item, status: 'failed', error: EXPIRED_ERROR };
          await store.set(key, expired);
          found.push(expired);
        } else {
          found.push(item);
        }
      }
    } catch {
      // Storage blocked: nothing was queued either.
    }
    if (this.scope === scope) this.publish(found);
  }

  /** Forgets the queue in memory (the device copy is deleted with every draft). */
  forget(): void {
    this.scope = null;
    this.publish([]);
  }

  find(kind: QueuedKind, target: string): QueuedAction | undefined {
    return this.items.find((i) => i.kind === kind && i.target === target);
  }

  /**
   * Keeps an action on this device to send later. Replaces any earlier item of the same kind
   * and target (the newest content wins). Refuses anything not in QUEUEABLE.
   */
  async enqueue(action: NewQueuedAction): Promise<QueuedAction> {
    const allowed = QUEUEABLE[action.kind] as (typeof QUEUEABLE)[QueuedKind] | undefined;
    if (allowed?.method !== action.method || !allowed.path.test(action.path)) {
      throw new NotQueueableError(`${action.method} ${action.path} can't be queued`);
    }
    if (this.scope === null) throw new NotQueueableError('Nobody is signed in');
    // Without storage the action would vanish on a refresh: refuse it, and say so.
    if (!deviceStore().available) throw new NotQueueableError('This device has no storage');
    const item: QueuedAction = {
      ...action,
      id: action.id ?? crypto.randomUUID(),
      savedAt: new Date().toISOString(),
      status: 'waiting',
      attempts: 0,
      error: null,
    };
    const store = deviceStore();
    const previous = this.find(action.kind, action.target);
    if (previous !== undefined && previous.id !== item.id)
      await store.delete(this.key(previous.id));
    await store.set(this.key(item.id), item);
    this.publish([...this.items.filter((i) => i.id !== previous?.id && i.id !== item.id), item]);
    return item;
  }

  /** Deletes an item: sent another way, or discarded by the person. */
  async remove(id: string): Promise<void> {
    await deviceStore()
      .delete(this.key(id))
      .catch(() => undefined);
    this.publish(this.items.filter((i) => i.id !== id));
  }

  async removeTarget(kind: QueuedKind, target: string): Promise<void> {
    const item = this.find(kind, target);
    if (item !== undefined) await this.remove(item.id);
  }

  private async update(item: QueuedAction): Promise<void> {
    await deviceStore()
      .set(this.key(item.id), item)
      .catch(() => undefined);
    this.publish(this.items.map((i) => (i.id === item.id ? item : i)));
  }

  /** Puts a failed item back in line, e.g. after "Try again". */
  async retry(id: string): Promise<void> {
    const item = this.items.find((i) => i.id === id);
    if (item === undefined || item.error?.code === EXPIRED_ERROR.code) return;
    await this.update({ ...item, status: 'waiting', attempts: 0, error: null });
  }

  /**
   * Sends the waiting items, oldest first. Stops at the first network pause (offline or
   * signed out) or transient server error; a refusal marks that item failed with the
   * server's reason and moves on. One run at a time.
   */
  flush(): Promise<SendResult> {
    this.flushing ??= this.run().finally(() => {
      this.flushing = null;
    });
    return this.flushing;
  }

  private async run(): Promise<SendResult> {
    const sent: QueuedAction[] = [];
    for (const queued of this.items.filter((i) => i.status === 'waiting')) {
      // Read it again: it may have been sent another way or discarded meanwhile.
      const item = this.items.find((i) => i.id === queued.id);
      if (item?.status !== 'waiting') continue;
      if (isExpired(item, Date.now())) {
        await this.update({ ...item, status: 'failed', error: EXPIRED_ERROR });
        continue;
      }
      try {
        await api(item.method, item.path, {
          body: item.body,
          headers: { 'Idempotency-Key': item.id },
        });
      } catch (error) {
        if (isNetworkPause(error)) break;
        const attempts = item.attempts + 1;
        if (isTransient(error) && attempts < MAX_ATTEMPTS) {
          await this.update({ ...item, attempts, error: errorOf(error) });
          break;
        }
        await this.update({ ...item, attempts, status: 'failed', error: errorOf(error) });
        continue;
      }
      if (item.clearDraftKey !== null) {
        await draftStore()
          .delete(item.clearDraftKey)
          .catch(() => undefined);
      }
      await this.remove(item.id);
      sent.push(item);
    }
    return { sent };
  }
}

/** The app's one queue. */
export const retryQueue = new RetryQueue();
